import { useContext, useState, useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import { Dialog, Bar } from '@ui5/webcomponents-react';
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ButtonOutlined from "../StyledComponents/ButtonOutlined";
import ButtonContained from "../StyledComponents/ButtonContained";
import StyledTextField from "../StyledComponents/StyledTextField";
import UserContext from "../../context/user-context";
import httpRequest from "../../httpRequest";

import { useStyles } from "./styles";
import USER_PIC from "../../images/user.png";

const ProfileModal = ({ open, onClose }) => {
  const classes = useStyles();
  const userCtx = useContext(UserContext);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [signature, setSignature] = useState(null);
  const [avatar, setAvatar] = useState(USER_PIC);
  const [showSignatureInput, setShowSignatureInput] = useState(false);
  const [showAvatarInput, setShowAvatarInput] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Refs for file inputs
  const signatureInputRef = useRef(null);
  const avatarInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName(userCtx.userName || "");
      setEmail(userCtx.email || "");
      setSignature(userCtx.signature || null);
      setAvatar(userCtx.avatar || USER_PIC);
    }
  }, [userCtx, open]);

  const handleClose = () => {
    onClose();
  };

  const handleSave = async () => {
    if (!validateEmail(email)) {
      enqueueSnackbar("Email is not valid!", { variant: "error" });
      return;
    }
  
    try {
      // Send a request to update user details
      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/update-user`, {
        name,
        email,
        signature,
        avatar,
      });
  
      // Update user context with new values
      userCtx.setUserData({
        name,
        email,
        signature,
        avatar,
      });
  
      // Clear old cached avatar and signature from local storage
      localStorage.removeItem("userAvatar");
      localStorage.removeItem("userSignature");
  
      // Set new avatar and signature in local storage
      if (avatar) {
        localStorage.setItem("userAvatar", avatar);
      }
      if (signature) {
        localStorage.setItem("userSignature", signature);
      }
  
      // Notify the user of success
      enqueueSnackbar("User details updated successfully", { variant: "success" });
      handleClose(); // Close the modal or any other UI element after saving
    } catch (error) {
      console.log(error);
    }
  };
  
  const validateEmail = (email) => {
    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/;
    return emailRegex.test(email);
  };

const handleSignatureUpload = (event) => {
  event.stopPropagation(); // Prevent propagation to parent components
  const file = event.target.files[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = () => {
      setSignature(reader.result);
      setShowSignatureInput(false);
      if (signatureInputRef.current) {
        signatureInputRef.current.value = ''; // Reset the input value
      }
    };
    reader.readAsDataURL(file);
  }
};

const handleAvatarUpload = (event) => {
  event.stopPropagation(); // Prevent propagation to parent components
  const file = event.target.files[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = () => {
      setAvatar(reader.result);
      setShowAvatarInput(false);
      if (avatarInputRef.current) {
        avatarInputRef.current.value = ''; // Reset the input value
      }
    };
    reader.readAsDataURL(file);
  }
};


  const handleAvatarDelete = () => {
    setAvatar(USER_PIC);
    setShowAvatarInput(false);
  };

  const triggerInput = (type) => {
    if (type === 'signature' && signatureInputRef.current) {
      signatureInputRef.current.click(); 
    } else if (type === 'avatar' && avatarInputRef.current) {
      avatarInputRef.current.click(); 
    }
  };

  return (
    <Dialog
      headerText="My Profile"
      open={open}
      onAfterClose={handleClose}
      footer={
        <Bar design="Footer" endContent={
          <>
            <ButtonOutlined style={{ marginRight: 8 }} onClick={handleClose}>
              Cancel
            </ButtonOutlined>
            <ButtonContained onClick={handleSave}>
              Save
            </ButtonContained>
          </>
        } />
      }
    >
      {/* Avatar Upload and Preview Section */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ position: 'relative', marginBottom: 20 }}>
          <img
            src={avatar}
            alt="Avatar Preview"
            style={{ width: 200, height: 200, borderRadius: '50%', objectFit: 'cover',
              objectPosition: 'center' }}
          />
          {!showAvatarInput && (
            <>
              <IconButton
                style={{ position: 'absolute', bottom: 0, right: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
                onClick={() => triggerInput('avatar')}
              >
                <EditIcon style={{ color: 'white' }} />
              </IconButton>
              {avatar !== USER_PIC && (
                <IconButton
                  style={{ position: 'absolute', bottom: 0, left: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
                  onClick={handleAvatarDelete}
                >
                  <DeleteIcon style={{ color: 'white' }} />
                </IconButton>
              )}
            </>
          )}
         <input
  type="file"
  accept="image/*"
  id="avatar-upload"
  onChange={handleAvatarUpload}
  ref={avatarInputRef}
  style={{ display: 'none' }} // Hide input visually
/>
        </div>
      </div>

      {/* Name and email fields */}
      <StyledTextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        margin="normal"
      />
      <StyledTextField
        label="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        fullWidth
        margin="normal"
      />

      {/* E-signature upload and preview section */}
      <div style={{ marginTop: 10, textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 10 }}>
          <Typography
            component="p"
            variant="body1"
            sx={{ fontFamily: "SapFiori72, sans-serif" }}
            style={{ marginRight: 10 }}
          >
            E-signature
          </Typography>
          <IconButton
            onClick={() => triggerInput('signature')}
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', color: 'white' }}
          >
            {signature ? <EditIcon /> : <AddCircleIcon />}
          </IconButton>
        </div>
        {signature && (
          <img
            src={signature}
            alt="Signature Preview"
            style={{ width: 100, marginTop: 2 }}
          />
        )}
        <input
  type="file"
  accept="image/*"
  id="signature-upload"
  onChange={handleSignatureUpload}
  ref={signatureInputRef}
  style={{ display: 'none' }} // Hide input visually
/>
      </div>
    </Dialog>
  );
};

export default ProfileModal;
